import { capitalize } from "lodash";
import { OBJECT_RIDE_MODES, ObjectRideModeEnum } from "app/data/helpers";
import {
  RideHistoryChangeDto,
  RideHistoryChangeType,
  RideHistoryItemDto
} from "domain/rides/entities/dtos/ride-history.dto";

// Loose on purpose: react-i18next's `t` is heavily overloaded and its return
// type widens to objects, so results are coerced with String() below.
export type Translate = (
  key: string,
  options?: Record<string, unknown>
) => unknown;

const hasValue = (value: string | null | undefined): value is string =>
  typeof value === "string" && value.trim() !== "";

const toTime = (date: string): number => {
  const time = new Date(date).getTime();
  return Number.isNaN(time) ? 0 : time;
};

export const sortHistoryItemsNewestFirst = (
  items: RideHistoryItemDto[]
): RideHistoryItemDto[] =>
  [...items].sort((a, b) => toTime(b.changedAt) - toTime(a.changedAt));

// The history endpoint returns enum names, the ride endpoints numbers; both
// are accepted so either contract renders the translated label.
const RIDE_MODE_BY_VALUE: Record<string, ObjectRideModeEnum> = {
  BUSINESS: ObjectRideModeEnum.BUSINESS,
  PRIVATE: ObjectRideModeEnum.PRIVATE,
  COMMUTE: ObjectRideModeEnum.COMMUTE,
  MIXED: ObjectRideModeEnum.MIXED,
  "1": ObjectRideModeEnum.BUSINESS,
  "2": ObjectRideModeEnum.PRIVATE,
  "3": ObjectRideModeEnum.COMMUTE,
  "4": ObjectRideModeEnum.MIXED
};

export const getRideModeLabel = (value: string, t: Translate): string => {
  const mode = RIDE_MODE_BY_VALUE[value.trim().toUpperCase()];
  const label = mode ? OBJECT_RIDE_MODES(t)[mode] : undefined;

  // The labels are uppercase in `en` (BUSINESS), which reads wrong mid-sentence.
  return label ? capitalize(label) : value;
};

interface DescribeOptions {
  format?: (value: string, t: Translate) => string;
  ignoreOldValue?: boolean;
  values?: Record<string, string>;
}

/**
 * Picks the translation variant that matches the values actually present, so
 * a missing value drops its part of the sentence instead of printing "null".
 */
const describe = (
  key: string,
  change: RideHistoryChangeDto,
  t: Translate,
  {
    format = (value) => value,
    ignoreOldValue = false,
    values
  }: DescribeOptions = {}
): string => {
  const oldValue =
    !ignoreOldValue && hasValue(change.oldValue)
      ? format(change.oldValue, t)
      : undefined;
  const newValue = hasValue(change.newValue)
    ? format(change.newValue, t)
    : undefined;

  const variant =
    oldValue && newValue
      ? "withOldAndNew"
      : newValue
        ? "withNewOnly"
        : oldValue
          ? "withOldOnly"
          : "withoutValues";

  return String(
    t(`${key}.${variant}`, {
      ...values,
      oldValue,
      newValue,
      // React escapes on render; escaping here too would show `&amp;`.
      interpolation: { escapeValue: false }
    })
  );
};

// The backend names the assignment through `field`; its values are whatever
// identifies the key or driver.
const ASSIGNMENT_KEYS: Record<string, string> = {
  KEYID: "rideHistory.changes.keyChanged",
  DRIVERID: "rideHistory.changes.driverChanged"
};

const normaliseField = (field: string | null): string =>
  (field ?? "").trim().toUpperCase();

const IDENTIFIER =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Raw identifiers must not reach the screen, so a value that is only a guid is
 * dropped and the change is announced without it.
 */
const withoutIdentifiers = (
  change: RideHistoryChangeDto
): RideHistoryChangeDto => {
  const keep = (value: string | null) =>
    hasValue(value) && IDENTIFIER.test(value.trim()) ? null : value;

  return {
    ...change,
    oldValue: keep(change.oldValue),
    newValue: keep(change.newValue)
  };
};

const CHANGE_DESCRIPTIONS: Record<
  RideHistoryChangeType,
  (change: RideHistoryChangeDto, t: Translate) => string
> = {
  RideTypeChanged: (change, t) =>
    describe("rideHistory.changes.rideTypeChanged", change, t, {
      format: getRideModeLabel
    }),
  PrivateMileageAdded: (change, t) =>
    describe("rideHistory.changes.privateMileageAdded", change, t, {
      ignoreOldValue: true
    }),
  DetourMileageAdded: (change, t) =>
    describe("rideHistory.changes.detourMileageAdded", change, t, {
      ignoreOldValue: true
    }),
  MileageCorrected: (change, t) =>
    describe("rideHistory.changes.mileageCorrected", change, t),
  // The text itself is shown through the item's reason/comment lines.
  CommentOrReasonAdded: (_change, t) =>
    String(t("rideHistory.changes.commentOrReasonAdded")),
  AssignmentChanged: (change, t) =>
    describe(
      ASSIGNMENT_KEYS[normaliseField(change.field)] ??
        "rideHistory.changes.assignmentChanged",
      withoutIdentifiers(change),
      t
    )
};

const isKnownChangeType = (type: string): type is RideHistoryChangeType =>
  Object.prototype.hasOwnProperty.call(CHANGE_DESCRIPTIONS, type);

export const getChangeDescription = (
  change: RideHistoryChangeDto,
  t: Translate
): string => {
  if (isKnownChangeType(change.type)) {
    return CHANGE_DESCRIPTIONS[change.type](change, t);
  }

  const field = hasValue(change.field)
    ? change.field
    : String(t("rideHistory.changes.generic.fieldFallback"));

  return describe("rideHistory.changes.generic", change, t, {
    values: { field }
  });
};
